<template>
  <NavigationWrapper :collapsed="isCollapseFeatureEnabled && areControlBarsCollapsed">
    <template #drawer>
      <Navigator v-show="isNavigatorDrawerOpen" />
    </template>

    <template #top>
      <CButton
        v-if="mode !== 'sharing' && !(pitcherSettings.alternative_canvas_fullscreen && isFullscreen)"
        v-tooltip="t('canvasUI.canvasBuilder.components.exit')"
        class="w-11 h-11 mt-3"
        :disabled="isSavingCanvas"
        size="large"
        strong
        type="primary"
        @click="onExitClick"
      >
        <template #icon>
          <CIcon class="fa-flip-horizontal" fa-type="fas" icon="right-from-bracket" size="18" />
        </template>
      </CButton>
    </template>

    <template #middle>
      <CButton
        v-if="isCollapseFeatureEnabled && !areControlBarsCollapsed"
        v-tooltip="t('canvasUI.canvasBuilder.components.collapseControlBars')"
        class="h-11 w-11"
        secondary
        size="large"
        strong
        @click="toggleControlBarsCollapse"
      >
        <template #icon>
          <CIcon icon="chevron-left" size="18" />
        </template>
      </CButton>

      <CButton
        v-tooltip="
          isNavigatorDrawerOpen
            ? t('canvasUI.canvasBuilder.components.closeTableOfContents')
            : t('canvasUI.canvasBuilder.components.openTableOfContents')
        "
        class="h-11 w-11"
        secondary
        size="large"
        strong
        :type="isNavigatorDrawerOpen ? 'primary' : undefined"
        @click="toggleNavigatorDrawer"
      >
        <template #icon>
          <CIcon icon="sidebar" size="18" />
        </template>
      </CButton>

      <CButton
        v-if="isBackToPreviousLocationVisible"
        v-tooltip="
          isBackToPreviousLocationDisabled
            ? t('canvasUI.canvasBuilder.components.backToPreviousLocationDisabled')
            : t('canvasUI.canvasBuilder.components.backToPreviousLocation')
        "
        class="h-11 w-11"
        :disabled="isBackToPreviousLocationDisabled"
        secondary
        size="large"
        strong
        @click="backToPreviousLocation"
      >
        <template #icon>
          <CIcon icon="backward" size="18" />
        </template>
      </CButton>

      <AnnotationMenu v-if="showAnnotationMenuButton" />
    </template>

    <template v-if="isEditMode" #bottom>
      <CTimelineControl />
    </template>

    <template v-else-if="showPreviousButton" #bottom>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.previousPage')"
        class="h-14 w-11"
        secondary
        size="large"
        strong
        @click="goToPreviousPage"
      >
        <template #icon>
          <CIcon icon="left" size="18" />
        </template>
      </CButton>
    </template>

    <template v-if="isCollapseFeatureEnabled && areControlBarsCollapsed" #toggle>
      <div
        class="fixed top-1/2 -translate-y-1/2 z-100000 transition-all duration-300 ease-in-out"
        :class="areControlBarsCollapsed ? 'left-0' : 'left-0'"
      >
        <CButton
          v-tooltip="
            areControlBarsCollapsed
              ? t('canvasUI.canvasBuilder.components.expandControlBars')
              : t('canvasUI.canvasBuilder.components.collapseControlBars')
          "
          class="h-11 w-4 b-rd-l-0"
          secondary
          strong
          style="background-color: rgba(255, 255, 255, 0.65)"
          @click="toggleControlBarsCollapse"
        >
          <template #icon>
            <CIcon class="color-primary" :icon="areControlBarsCollapsed ? 'chevron-right' : 'chevron-left'" size="18" />
          </template>
        </CButton>
      </div>
    </template>
  </NavigationWrapper>
</template>

<script setup lang="tsx">
import { computed, type ComputedRef, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTimelineControl from '@lib/components/CTimeline/CTimelineControl.vue'
import NavigationWrapper from '@canvas-builder/components/container/NavigationWrapper/NavigationWrapper.vue'
import Navigator from '@canvas-builder/components/container/Navigator/Navigator.vue'
import AnnotationMenu from '@canvas-builder/components/container/AnnotationMenu/AnnotationMenu.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useNavigatorDrawer from '@canvas-builder/composables/useNavigatorDrawer'
import useControlBarCollapse from '@canvas-builder/composables/useControlBarCollapse'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import tooltipDirective from '@lib/directives/tooltip'
import useCanvasExit from '@canvas-builder/composables/useCanvasExit'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const { t } = useI18n()
const vTooltip = tooltipDirective

const isSavingCanvas = ref(false)
const { mode, isEditMode, isDsr, isFullscreen } = useCanvas()
const { onExitClick } = useCanvasExit()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { toggleNavigatorDrawer, isNavigatorDrawerOpen } = useNavigatorDrawer()
const { toggleControlBarsCollapse, areControlBarsCollapsed } = useControlBarCollapse()
const {
  goToPreviousPage,
  isFirstPage,
  backToPreviousLocation,
  isBackToPreviousLocationVisible,
  isBackToPreviousLocationDisabled,
} = useCanvasPages()

const showPreviousButton = computed(
  () =>
    (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' &&
    !isEditMode.value &&
    !isFirstPage.value,
)
const showAnnotationMenuButton = computed((): boolean => {
  const settings = pitcherSettings.value
  return (
    !!settings.enable_presentation_annotations &&
    !isDsr.value &&
    !isEditMode.value &&
    (!isFullscreen.value || (isFullscreen.value && !!settings.alternative_canvas_fullscreen))
  )
})

const isCollapseFeatureEnabled = computed(() => !!launchDarkly.value.enable_canvas_control_bars_collapse)
</script>
